import Action from 'actions';
import BaseLP from 'services/sports/default/base-lp';
import Const from 'constants';
import ArrayHelper from 'helpers/array-helper';
import {getSimplexUnitName} from 'helpers/simplex-helper';

export default class LpClassic extends BaseLP {
    createGroupsOfStacks({incomingValues: {dispatch}}) {
        const {
            playersData: {preparedPlayers},
        } = Action.getState();
        dispatch(Action.do('createGroupsOfPositionStacks', preparedPlayers.groupedBy.teamAndPosition()));
        dispatch(Action.do('createGroupsOfTeamStacks', preparedPlayers.groupedBy.property('team')));
    }

    limitPositions({incomingValues = {}}) {
        const sportConfig = Action.getState('sportConfig', {});
        const {teams = {}} = incomingValues;

        let maxSingleTeamPlayers = sportConfig.getConfigProp('maxSingleTeamPlayers'),
            result = '';

        if (maxSingleTeamPlayers) {
            Object.keys(teams).map((teamName) => {
                let str = '',
                    addedPlayers = [];

                teams[teamName].map((player) => {
                    if (inArray(player.uniqueId, addedPlayers)) {
                        return;
                    }

                    str += '+' + getSimplexUnitName(player.uniqueId);
                    addedPlayers.push(player.uniqueId);
                });

                if (str) {
                    result += str + '<=' + maxSingleTeamPlayers + '\n';
                }
            });
        }

        return result;
    }

    getInitialOptimizationConfig() {
        const state = Action.getState();
        const {lineupsQuantity} = state;
        const {flexMaxExposurePositions, superFlexMaxExposurePositions} = state.playersData;
        let result = {
            [Const.POSITION_FLEX]: {},
            [Const.POSITION_SUPERFLEX]: {},
        };
        let threshold = Math.ceil(100 / lineupsQuantity.getLineupsQuantity());
        Object.keys(flexMaxExposurePositions).map((posName) => {
            let usageQuantity = flexMaxExposurePositions[posName];
            if (Const.DEFAULT_VALUE_FOR_MAX_EXPOSURE > usageQuantity) {
                let percent = flexMaxExposurePositions[posName];
                if (percent > 0) {
                    result[Const.POSITION_FLEX][posName] = Math.ceil(usageQuantity / threshold);
                } else {
                    result[Const.POSITION_FLEX][posName] = percent;
                }
            }
        });
        Object.keys(superFlexMaxExposurePositions).map((posName) => {
            let usageQuantity = superFlexMaxExposurePositions[posName];
            if (Const.DEFAULT_VALUE_FOR_MAX_EXPOSURE > usageQuantity) {
                let percent = superFlexMaxExposurePositions[posName];
                if (percent > 0) {
                    result[Const.POSITION_SUPERFLEX][posName] = Math.ceil(usageQuantity / threshold);
                } else {
                    result[Const.POSITION_SUPERFLEX][posName] = percent;
                }
            }
        });
        // console.log('result', {...result});
        return result;
    }

    createRunSettingsFlexPositionsKey({incomingValues: {lineupId}}) {
        return `flexPositions-${lineupId}`;
    }

    handleSettingsBasedOnLineup({incomingValues: {simplexLineup, readyLineupsQuantity, dispatch}}) {
        const {sportConfig, optimizationConfig} = Action.getState();
        const flexPositions = sportConfig.getConfigProp('flexPlayerPositionName');
        const currentConfig = optimizationConfig.getConfig();

        // #22898 DK CFB: error in console and generated lineups are not shown when Flex settings are not default
        dispatch(
            Action.set(
                'RunSettings',
                this.exec('createRunSettingsFlexPositionsKey', {
                    lineupId: simplexLineup.getId(),
                }),
                currentConfig,
            ),
        );

        const lineupPlayers = simplexLineup.getUnits().map((item) => {
            return this.exec('preparePlayerByLPPlayerId', {id: item});
        });

        const players = dispatch(
            Action.do('prepareLineupPlayers', {
                lineupPlayers,
                lineupNumber: readyLineupsQuantity,
                lineupId: simplexLineup.getId(),
            }),
        );

        const newConfig = {};
        players.map((player) => {
            if (inArray(player.positionInLineupRaw, flexPositions)) {
                const posflex = player.positionInLineupRaw;
                let availableUsage = 0;

                if (currentConfig[posflex].hasOwnProperty(player.positionName)) {
                    availableUsage = Number(currentConfig[posflex][player.positionName]) - 1;
                    let val = availableUsage < 0 ? 0 : availableUsage;
                    newConfig[posflex] = Object.assign({}, currentConfig[posflex], {[player.positionName]: val});
                }
            }
        });

        const config = Object.assign({}, currentConfig, newConfig);

        dispatch(Action.set('OptimizationConfig', config));
    }

    defineFlexPositionsInLineupByPositionsOrder({incomingValues: {lineup, type}}) {
        let playersId = [...lineup];
        const state = Action.getState();
        const {sportConfig} = state;
        const {preparedPlayers} = state.playersData;
        const players = preparedPlayers.groupedBy.playerId();
        let positionsOrder = sportConfig.getConfigProp('positionsOrder', {});

        Object.keys(positionsOrder).map((key) => {
            let position = positionsOrder[key];
            for (let i in playersId) {
                let player = players[playersId[i]];

                if (
                    position.alias === type &&
                    !arrayDuplicates(position.allowedPositions, player.position).length > 0
                ) {
                    delete playersId[i];
                    break;
                }

                if (position.alias === player.positionName) {
                    delete playersId[i];
                    break;
                }
            }
        });

        let result = [];
        playersId.map((pId) => {
            let player = players[pId];
            result.push(player.positionName);
        });

        return result;
    }

    getForbiddenFlexMaxExposurePositions({incomingValues: {optimizationConfig, positionName}}) {
        const result = {};
        if (optimizationConfig.hasOwnProperty(positionName)) {
            const constraint = optimizationConfig[positionName];
            Object.keys(constraint).map((posName) => {
                if (constraint[posName] === 0) {
                    result[posName] = 1;
                }
            });
        }
        return {...result};
    }

    prepareSectionByPositionWithSuperFlexMaxExposure({incomingValues = {}}) {
        const {
            sportConfig,
            optimizationConfig,
            playersData: {preparedPlayers},
        } = Action.getState();
        const players = preparedPlayers.groupedBy.allowedPositions();

        const forbiddenFlexPositions = this.exec('getForbiddenFlexMaxExposurePositions', {
            optimizationConfig: optimizationConfig.getConfig(),
            positionName: Const.POSITION_FLEX,
        });
        const forbiddenSuperFlexPositions = this.exec('getForbiddenFlexMaxExposurePositions', {
            optimizationConfig: optimizationConfig.getConfig(),
            positionName: Const.POSITION_SUPERFLEX,
        });

        let flexPositions = sportConfig.getConfigProp('flexPlayerPosition');
        let positions = sportConfig.getConfigProp('positions');
        let flexPosition = positions[flexPositions[Const.POSITION_FLEX]];
        let superFlexPosition = positions[flexPositions[Const.POSITION_SUPERFLEX]];
        let decrementFlex = false;
        let decrementSuperFlex = false;

        let result = '';
        Object.keys(positions).map((pos) => {
            let playersOnPos = {};
            let addedPlayers = [];
            let posObj = positions[pos];
            let isPositionFlex = posObj.id === 'flex';

            players.map((player) => {
                if (!inArray(player.position, posObj.allowedPositions) || inArray(player.uniqueId, addedPlayers)) {
                    return;
                }

                if (!playersOnPos.hasOwnProperty(player.team)) {
                    playersOnPos[player.team] = '';
                }

                playersOnPos[player.team] += '+' + getSimplexUnitName(player.uniqueId);

                addedPlayers.push(player.uniqueId);
            });

            if (isEmpty(playersOnPos)) {
                return;
            }

            let str = '';
            Object.keys(playersOnPos).map((teamName) => {
                if (posObj.maxFromOneTeam) {
                    result += playersOnPos[teamName] + '<=' + posObj.maxFromOneTeam + '\n';
                } else {
                    str += playersOnPos[teamName];
                }
            });

            if (str) {
                let forbiddenMaxPlayersQuantity = 0;
                let forbiddenMinPlayersQuantity = 0;

                if (!isPositionFlex) {
                    if (!isEmpty(forbiddenFlexPositions)) {
                        let countFlex = ArrayHelper.countItems(forbiddenFlexPositions);
                        if (inArray(posObj.shortName, Object.keys(forbiddenFlexPositions))) {
                            forbiddenMaxPlayersQuantity += forbiddenFlexPositions[posObj.shortName];
                            //case when all value are 0
                            if (flexPosition.allowedPositions.length === countFlex) {
                                decrementFlex = true;
                            }
                        } else if (
                            flexPosition.allowedPositions.length - 1 === countFlex &&
                            arrayDuplicates(posObj.allowedPositions, flexPosition.allowedPositions).length > 0
                        ) {
                            // position in allowed array
                            forbiddenMinPlayersQuantity++;
                        }
                    }

                    if (!isEmpty(forbiddenSuperFlexPositions)) {
                        let countSuperFlex = ArrayHelper.countItems(forbiddenSuperFlexPositions);
                        if (inArray(posObj.shortName, Object.keys(forbiddenSuperFlexPositions))) {
                            forbiddenMaxPlayersQuantity += forbiddenSuperFlexPositions[posObj.shortName];
                            //case when all value are 0
                            if (superFlexPosition.allowedPositions.length === countSuperFlex) {
                                decrementSuperFlex = true;
                            }
                        } else if (
                            superFlexPosition.allowedPositions.length - 1 === countSuperFlex &&
                            arrayDuplicates(posObj.allowedPositions, superFlexPosition.allowedPositions).length > 0
                        ) {
                            forbiddenMinPlayersQuantity++;
                        }
                    }
                }

                if (isPositionFlex && (decrementFlex || decrementSuperFlex)) {
                    forbiddenMaxPlayersQuantity++;
                }

                if (false !== Boolean(posObj.minPlayers)) {
                    result += str + '=>' + (posObj.minPlayers + forbiddenMinPlayersQuantity) + '\n';
                }
                if (false !== Boolean(posObj.maxPlayers)) {
                    result += str + '<=' + (posObj.maxPlayers - forbiddenMaxPlayersQuantity) + '\n';
                }
            }
        });

        return result;
    }

    /**
     * DO NOT use prepareSectionByPosition and prepareSectionByPositionWithSuperFlexMaxExposure together
     */
    subjectToPermanent() {
        const constraints = {
            salary: this.exec('prepareSectionBySalary', {}, ''),
            projectedOwnershipCap: this.exec('projectedOwnership', {}, ''),
            teams: this.exec('prepareSectionByTeam', {}, ''),
            games: this.exec('prepareSectionByGame', {}, ''),
            keyPlayerStacking: this.exec('keyPlayerStacking', {}, ''),
            teamNameStacking: this.exec('teamNameStacking', {}, ''),
            passReceiver: this.exec('prepareSectionByPassReceiver', {}, ''),
            minGames: this.exec('prepareSectionByMinGames2AndMinTeams2', {addPositionToPID: true}, ''),
            projectionBoostRule: this.exec('projectionBoostRule', {}, ''),
        };

        return {...constraints};
    }

    subjectToAlterable() {
        const constraints = {
            playersWhoseGamesAreLocked: this.exec('playersWhoseGamesAreLocked', {}, ''),
            createdLineups: this.exec('prepareSectionByCreatedLineups', {}, ''),
            exposureMax: this.exec('prepareSectionByExposureMax', {}, ''),
            exposureMin: this.exec('prepareSectionByExposureMin', {}, ''),
            flexExposureMax: this.exec('prepareSectionByPositionWithSuperFlexMaxExposure', {}, ''),
            playerCombinationStacking: this.exec('playerCombinationStacking', {}, ''),
        };

        return {...constraints};
    }
}
